/**
 * DraftReconcileDialog
 *
 * Opens when the user clicks "Edit" on a draft in ImportQueue.
 * Shows the email snippet + parsed fields on the left, and a reconcile card
 * (mirroring StatementImport's reconcile phase) on the right so the user can
 * confirm, edit, or create a new budget entry before accepting the draft.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { AddCircleOutline, CheckCircleOutline, Edit } from '@material-ui/icons';
import { ImportDraft } from '../core/services/import.service';
import {
    buildMatches,
    DraftEntry,
    MatchResult,
    StatementRow,
} from './statement.utils';
import { EntryFormDialog, EntryFormValues } from './EntryFormDialog';

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() =>
    createStyles({
        dialogContent: {
            display: 'flex',
            gap: 16,
            padding: '16px 20px',
            minHeight: 320,
        },
        leftPanel: {
            flex: '0 0 220px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
        },
        rightPanel: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
        },
        sectionLabel: {
            fontSize: '0.65rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1,
            color: '#94a3b8',
            marginBottom: 4,
        },
        snippetBox: {
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '8px 10px',
            fontSize: '0.72rem',
            color: '#475569',
            fontStyle: 'italic',
            lineHeight: 1.5,
            wordBreak: 'break-word',
        },
        parsedField: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '3px 0',
        },
        parsedLabel: {
            fontSize: '0.7rem',
            color: '#94a3b8',
        },
        parsedValue: {
            fontSize: '0.78rem',
            color: '#1e293b',
            fontWeight: 500,
            textAlign: 'right',
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        matchCard: {
            borderRadius: 8,
            padding: '10px 14px',
            background: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
            borderLeft: '3px solid transparent',
        },
        matchCardConfirmed: {
            borderLeftColor: '#16a34a',
            background: '#f0fdf4',
        },
        matchCardCreate: {
            borderLeftColor: '#2563eb',
            background: '#eff6ff',
        },
        matchTop: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
        },
        matchStmt: {
            flex: '0 0 140px',
        },
        matchArrow: {
            flex: '0 0 48px',
            textAlign: 'center' as const,
            paddingTop: 4,
        },
        matchEntry: {
            flex: 1,
        },
        matchActions: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 8,
            flexWrap: 'wrap' as const,
        },
        scoreText: {
            fontSize: 11,
            fontWeight: 700,
        },
    })
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const scoreColor = (s: number) =>
    s >= 0.7 ? '#16a34a' : s >= 0.4 ? '#d97706' : '#94a3b8';

const toDateStr = (d: Date | null): string =>
    d ? d.toISOString().slice(0, 10) : '';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DraftReconcileDialogProps {
    open: boolean;
    draft: ImportDraft;
    listId: string;
    entries: any[];
    existingCategories: string[];
    existingTags: string[];
    onAccept: (draftId: string, listId: string, editedValues?: { title: string; amount: number; type: 'DEBIT' | 'CREDIT'; date?: string; category?: string; tag?: string; onCredit?: boolean }) => void;
    onDismiss: (draftId: string) => void;
    onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * DraftReconcileDialog shows the email context and a reconcile card for a
 * single import draft. It lets the user confirm a match against an existing
 * budget entry, create a new entry, or dismiss the draft entirely.
 */
export const DraftReconcileDialog: React.FC<DraftReconcileDialogProps> = ({
    open,
    draft,
    listId,
    entries,
    existingCategories,
    existingTags,
    onAccept,
    onDismiss,
    onClose,
}) => {
    const classes = useStyles();

    // Build the single-row StatementRow representation of this draft.
    // Amount is negated for DEBIT so the sign convention matches bank statement rows.
    const statementRow = useMemo<StatementRow>(() => ({
        date: draft.date ? new Date(draft.date.slice(0, 10) + 'T12:00:00') : null,
        description: draft.title,
        amount: draft.type === 'DEBIT' ? -Number(draft.amount) : Number(draft.amount),
        rawDate: draft.date ? draft.date.slice(0, 10) : '',
        index: 0,
    }), [draft]);

    // Run the same matching logic used in StatementImport reconcile phase.
    const initialMatch = useMemo<MatchResult>(
        () => buildMatches([statementRow], entries)[0],
        [statementRow, entries]
    );

    // Local reconcile state — mirrors the per-row state in StatementImport
    const [match, setMatch] = useState<MatchResult>(initialMatch);

    // Re-initialise whenever the dialog opens with a different draft
    useEffect(() => {
        if (open) {
            setMatch(buildMatches([statementRow], entries)[0]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // EntryFormDialog state
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState<'confirm' | 'create'>('create');
    const [formInitial, setFormInitial] = useState<Partial<EntryFormValues>>({});

    // Parsed email fields from API (populated by backend during Gmail sync)
    const parsed = {
        merchant: draft.parsedMerchant ? String(draft.parsedMerchant) : null,
        amount: draft.parsedAmount != null ? Number(draft.parsedAmount) : null,
        date: draft.parsedDate ? String(draft.parsedDate) : null,
        account: draft.parsedAccount ? String(draft.parsedAccount) : null,
    };

    const snippet = draft.rawSnippet || '';

    // ── Card derived values ────────────────────────────────────────────────────

    const { statement: stmt, matchedEntry, score, confirmed, createEntry, skipped, draftEntry } = match;
    const stmtAmount = Number(stmt.amount) || 0;
    const amtColor = stmtAmount > 0 ? '#16a34a' : '#dc2626';
    const amtStr = `${stmtAmount > 0 ? '+' : ''}$${Math.abs(stmtAmount).toFixed(2)}`;

    const cardClassName = [
        classes.matchCard,
        confirmed ? classes.matchCardConfirmed : '',
        createEntry ? classes.matchCardCreate : '',
    ].filter(Boolean).join(' ');

    // ── Form helpers ──────────────────────────────────────────────────────────

    const openConfirmForm = () => {
        const entry = matchedEntry;
        const draft_ = draftEntry;
        setFormInitial({
            title: draft_?.title || entry?.title || stmt.description || '',
            amount: draft_?.amount || String(entry?.amount || Math.abs(stmt.amount)),
            type: draft_?.type || entry?.type || (stmt.amount > 0 ? 'CREDIT' : 'DEBIT'),
            category: draft_?.category || entry?.category || '',
            tag: draft_?.tag || entry?.tag || '',
            date: draft_?.date || (entry?.date ? String(entry.date).slice(0, 10) : toDateStr(stmt.date)),
        });
        setFormMode('confirm');
        setFormOpen(true);
    };

    const openCreateForm = () => {
        const draft_ = draftEntry;
        setFormInitial({
            title: draft_?.title || stmt.description || '',
            amount: draft_?.amount || String(Math.abs(stmt.amount)),
            type: draft_?.type || (stmt.amount > 0 ? 'CREDIT' : 'DEBIT'),
            category: draft_ ? draft_.category : (matchedEntry?.category || ''),
            tag: draft_ ? draft_.tag : (matchedEntry?.tag || ''),
            date: draft_?.date || toDateStr(stmt.date),
        });
        setFormMode('create');
        setFormOpen(true);
    };

    const handleFormSave = (values: EntryFormValues) => {
        const draftValues: DraftEntry = { ...values };
        if (formMode === 'confirm') {
            setMatch(prev => ({
                ...prev,
                confirmed: true,
                skipped: false,
                createEntry: false,
                draftEntry: draftValues,
            }));
        } else {
            setMatch(prev => ({
                ...prev,
                createEntry: true,
                skipped: false,
                confirmed: false,
                draftEntry: draftValues,
            }));
        }
        setFormOpen(false);
    };

    // ── Dialog actions ────────────────────────────────────────────────────────

    const handleAccept = () => {
        const edited = match.draftEntry;
        if (edited) {
            // Pass edited values so the caller can update the draft before accepting
            onAccept(draft.id, listId, {
                title: edited.title,
                amount: Number(edited.amount),
                type: edited.type as 'DEBIT' | 'CREDIT',
                date: edited.date || undefined,
                category: edited.category || undefined,
                tag: edited.tag || undefined,
                onCredit: edited.onCredit,
            });
        } else {
            onAccept(draft.id, listId);
        }
    };

    const handleDismiss = () => {
        onDismiss(draft.id);
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableEnforceFocus>
                <DialogTitle>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Chip
                            label={draft.source}
                            size="small"
                            style={{
                                fontSize: '0.65rem',
                                height: 18,
                                backgroundColor: '#ede9fe',
                                color: '#7c3aed',
                                fontWeight: 700,
                            }}
                        />
                        <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>
                            Review import
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    {/* ── Left: email context ── */}
                    <Box className={classes.leftPanel}>
                        <Typography className={classes.sectionLabel}>Email</Typography>

                        {snippet ? (
                            <Box className={classes.snippetBox}>{snippet}</Box>
                        ) : (
                            <Typography style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                No snippet available
                            </Typography>
                        )}

                        <Divider style={{ marginTop: 4 }} />

                        <Typography className={classes.sectionLabel}>Parsed fields</Typography>

                        <Box>
                            {[
                                { label: 'Merchant', value: parsed.merchant },
                                {
                                    label: 'Amount',
                                    value: parsed.amount !== null ? `$${parsed.amount.toFixed(2)}` : null,
                                },
                                { label: 'Date', value: parsed.date },
                                { label: 'Account', value: parsed.account },
                            ].map(({ label, value }) => (
                                <Box key={label} className={classes.parsedField}>
                                    <Typography className={classes.parsedLabel}>{label}</Typography>
                                    <Typography
                                        className={classes.parsedValue}
                                        title={value || ''}
                                    >
                                        {value || <span style={{ color: '#cbd5e1' }}>—</span>}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* ── Right: reconcile card ── */}
                    <Box className={classes.rightPanel}>
                        <Typography className={classes.sectionLabel}>Match</Typography>

                        <Paper className={cardClassName} elevation={0}>
                            {/* Top: statement | arrow + score | entry */}
                            <Box className={classes.matchTop}>
                                {/* Statement side */}
                                <Box className={classes.matchStmt}>
                                    <Typography variant="caption" style={{ color: '#94a3b8', display: 'block' }}>
                                        {stmt.rawDate}
                                    </Typography>
                                    <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#1e293b', marginBottom: 2 }}>
                                        {stmt.description}
                                    </Typography>
                                    <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: amtColor }}>
                                        {amtStr}
                                    </Typography>
                                </Box>

                                {/* Arrow + score */}
                                <Box className={classes.matchArrow}>
                                    <Typography
                                        className={classes.scoreText}
                                        style={{ color: scoreColor(score) }}
                                    >
                                        {matchedEntry ? `${Math.round(score * 100)}%` : '—'}
                                    </Typography>
                                    <Typography style={{ fontSize: 18, color: '#cbd5e1' }}>→</Typography>
                                </Box>

                                {/* Entry side */}
                                <Box className={classes.matchEntry}>
                                    {confirmed && draftEntry ? (
                                        <>
                                            <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#15803d', marginBottom: 2, fontWeight: 600 }}>
                                                {draftEntry.title}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#64748b' }}>
                                                ${Number(draftEntry.amount).toFixed(2)} · {draftEntry.type}
                                                {draftEntry.tag ? ` · ${draftEntry.tag}` : ''}
                                            </Typography>
                                        </>
                                    ) : createEntry && draftEntry ? (
                                        <>
                                            <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#1d4ed8', marginBottom: 2, fontStyle: 'italic' }}>
                                                {draftEntry.title}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#64748b' }}>
                                                ${Number(draftEntry.amount).toFixed(2)} · {draftEntry.type}
                                                {draftEntry.tag ? ` · ${draftEntry.tag}` : ''}
                                            </Typography>
                                        </>
                                    ) : matchedEntry ? (
                                        <>
                                            <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#1e293b', marginBottom: 2 }}>
                                                {matchedEntry.title}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#64748b' }}>
                                                ${Number(matchedEntry.amount).toFixed(2)} · {matchedEntry.type}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                            No match found
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Actions row */}
                            <Box className={classes.matchActions}>
                                {/* Confirm — available when matched and not yet confirmed/creating */}
                                {!skipped && !confirmed && !createEntry && matchedEntry && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={openConfirmForm}
                                        style={{ minWidth: 80 }}
                                    >
                                        Confirm
                                    </Button>
                                )}

                                {/* Confirmed chip — click to re-edit */}
                                {!skipped && confirmed && (
                                    <Chip
                                        size="small"
                                        icon={<CheckCircleOutline style={{ fontSize: 14 }} />}
                                        label="Confirmed"
                                        style={{ backgroundColor: '#dcfce7', color: '#15803d', fontSize: 11 }}
                                        onClick={openConfirmForm}
                                        deleteIcon={<Edit style={{ fontSize: 12 }} />}
                                        onDelete={openConfirmForm}
                                    />
                                )}

                                {/* Create — available when not confirmed and not already creating */}
                                {!skipped && !confirmed && !createEntry && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<AddCircleOutline style={{ fontSize: 14 }} />}
                                        onClick={openCreateForm}
                                    >
                                        Create new
                                    </Button>
                                )}

                                {/* Will create chip — click to re-edit */}
                                {!skipped && createEntry && (
                                    <Chip
                                        size="small"
                                        icon={<AddCircleOutline style={{ fontSize: 14 }} />}
                                        label="Will create"
                                        style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: 11 }}
                                        onClick={openCreateForm}
                                        deleteIcon={<Edit style={{ fontSize: 12 }} />}
                                        onDelete={openCreateForm}
                                    />
                                )}
                            </Box>
                        </Paper>
                    </Box>
                </DialogContent>

                <DialogActions style={{ padding: '8px 20px 16px', gap: 8 }}>
                    <Button onClick={onClose} style={{ color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleDismiss}
                        style={{ borderColor: '#dc2626', color: '#dc2626' }}
                    >
                        Dismiss
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAccept}
                        style={{ backgroundColor: '#7c3aed', color: 'white' }}
                    >
                        Accept
                    </Button>
                </DialogActions>
            </Dialog>

            <EntryFormDialog
                open={formOpen}
                dialogTitle={formMode === 'confirm' ? 'Edit confirmed entry' : 'Create new entry'}
                saveLabel={formMode === 'confirm' ? 'Confirm' : 'Create'}
                initialValues={formInitial}
                existingCategories={existingCategories}
                existingTags={existingTags}
                onSave={handleFormSave}
                onClose={() => setFormOpen(false)}
            />
        </>
    );
};
